import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type CombinationSupport = 'sufficient' | 'insufficient'

interface CombinationItem {
    id?: string
    name?: string
    nPocs?: number
    support?: CombinationSupport
}

interface MteCardDataItem {
    id?: string
    Level?: string
    nPocs?: number
    Converted_Exp_Unit_Treatment?: number
    support?: CombinationSupport
    Exp_Status?: string
}

interface Props {
    combinations?: CombinationItem[] | null
    activatedPocs?: number
    availableResults?: number
    mteCardData?: MteCardDataItem[] | null
    miniview?: boolean
}

const MteCards: React.FC<Props> = ({ combinations = [], activatedPocs, availableResults, mteCardData = null, miniview = false }) => {
    const { t } = useTranslation()

    const combinationItems = useMemo<CombinationItem[]>(() => {
        //.includes('Inconclusive') item.Exp_Status === 'Inconclusive-Sample_Insufficiency'
        if (mteCardData?.length) {
            return mteCardData.map((item) => ({
                id: item.id ?? item.Level,
                name: item.Level,
                nPocs: item.nPocs ?? item.Converted_Exp_Unit_Treatment ?? 0,
                support: item.Exp_Status?.includes('Inconclusive') ? 'insufficient' : 'sufficient',
            }))
        }

        return combinations ?? []
    }, [combinations, mteCardData])

    const resolvedActivatedPocs = useMemo(() => {
        if (activatedPocs != null) return activatedPocs

        return combinationItems.reduce((sum, item) => sum + (item.nPocs ?? 0), 0)
    }, [activatedPocs, combinationItems])

    const resolvedAvailableResults = useMemo(() => {
        if (availableResults != null) return availableResults

        return combinationItems.filter((item) => item.support === 'sufficient').length
    }, [availableResults, combinationItems])

    return (
        <div className={`grid grid-cols-3 gap-3 ${miniview ? 'text-[.6rem]' : 'text-[1rem]'}`} data-aos="zoom-in" data-aos-delay="200">
            <article className="rounded-[0.5rem] bg-[#FFE600] px-3 py-2">
                <span className="mb-1 block text-[0.75em] leading-[1.4] font-bold text-[#111315]">{t('Exact Combinations')}</span>
                {!miniview && <span className="mb-2 block text-[0.583em] leading-[1] font-medium text-[#474B50]">{t('observed in the selected Analysis Range')}</span>}
                <strong className="block text-[1.4em] font-bold leading-none text-[#111315]">{combinationItems.length}</strong>
            </article>

            <article className="rounded-[0.5rem] bg-[#FFE600] px-3 py-2">
                <span className="mb-1 block text-[0.75em] leading-[1.4] font-bold text-[#111315]">{t('Activated POCs')}</span>
                {!miniview && <span className="mb-2 block text-[0.583em] leading-[1] font-medium text-[#474B50]">{t('across the exact combinations below')}</span>}
                <strong className="block text-[1.4em] font-bold leading-none text-[#111315]">{resolvedActivatedPocs.toLocaleString('en-US')}</strong>
            </article>

            <article className="rounded-[0.5rem] bg-[#FFE600] px-3 py-2">
                <span className="mb-1 block text-[0.75em] leading-[1.4] font-bold text-[#111315]">{t('Results Available')}</span>
                {!miniview && <span className="mb-2 block text-[0.583em] leading-[1] font-medium text-[#474B50]">{t('combinations with a conclusive impact')}</span>}
                <strong className="block text-[1.4em] font-bold leading-none text-[#111315]">
                    {resolvedAvailableResults}
                    <span className="ml-1 text-sm font-bold leading-none">{t('of {{count}}', { count: combinationItems.length })}</span>
                </strong>
            </article>
        </div>
    )
}

export default MteCards
